import * as React from "react";
import { Component, createContext, FormEvent } from "react";
import { RouteComponentProps } from "react-router";
import * as Axios from "axios";
import GrammarApi from "../../api/GrammarApi";
import MessageService from "../../service/MessageService";
import CancelableAxiosInstance from "../../api/CancelableAxiosInstance";
import { ActivityWatcher } from "./ActivityWatcher";
import CornerstoneUtil from "../../util/CornerstoneUtil";
import { Trans } from "react-i18next";
import i18n from "i18next";
import moment = require("moment");

export const AUTH_HTTP_HEADER = "Authorization";
const STORAGE_TOKEN_KEY = "authToken";
const STORAGE_TOKEN_EXP_KEY = "authTokenExpiration";
const STORAGE_AUTH_ROLE_KEY = "authRoles";
const STORAGE_LOGIN_KEY = "authLogin";

export interface WhoAmI {
  login: string;
}

export interface AuthManager {
  isAuth: boolean;
  whoAmI: () => WhoAmI;
  logout: () => void;
  isRole: (role: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (role: string[]) => boolean;
  cancelableClient: () => CancelableAxiosInstance;
}

interface RenewedToken {
  token: string;
  expireAt: string;
}

export const AuthContext = createContext<AuthManager>(null);

export class AuthParent
  extends Component<
    {},
    {
      _isAuth: boolean;
      login: string;
      password: string;
      expiration: moment.Moment;
    }
  >
  implements AuthManager
{
  private axiosInstance: CancelableAxiosInstance;

  constructor(props: RouteComponentProps<string>) {
    super(props);
    this.axiosInstance = this.cancelableClient();
    this.state = {
      _isAuth: this.isAuth,
      login: "",
      password: "",
      expiration: moment(new Date()),
    };
  }

  cancelableClient(): CancelableAxiosInstance {
    const client = Axios.default.create({
      baseURL: "/api",
      headers: { common: { "Content-Type": "application/json" } },
    });

    client.interceptors.request.use((config) => {
      const tok = localStorage.getItem(STORAGE_TOKEN_KEY);
      if (tok) config.headers[AUTH_HTTP_HEADER] = "Bearer " + tok;
      return config;
    });

    client.interceptors.response.use(
      (resp) => resp,
      (error) => {
        if (error.response && error.response.status === 401) {
          this.logout();
        }

        return Promise.reject(error);
      }
    );

    client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error && error.message === CancelableAxiosInstance.CANCEL_REQUEST_ERROR_MESSAGE) {
          return Promise.reject(error);
        }

        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx

          if (error.response.status !== 401 && error.response.status !== 404) {
            MessageService.error(i18n.t("error.occurred"));
          }

          console.error("An http error occurred : ", {
            data: error.response.data,
            status: error.response.status,
            headers: error.response.headers,
          });
        } else if (error.request) {
          console.error("An http error occurred / request: ", error.request);
        } else {
          // Something happened in setting up the request that triggered an Error
          console.error("An http error occurred : message", error);
        }

        return Promise.reject(error);
      }
    );

    return new CancelableAxiosInstance(client);
  }

  componentDidMount() {
    this.refresh();
    this.setState({
      expiration: moment(new Date(localStorage.getItem(STORAGE_TOKEN_EXP_KEY))),
    });
    this.onNewToken(localStorage.getItem(STORAGE_TOKEN_KEY));
  }

  refresh() {
    if (this.isAuth) {
      const api: GrammarApi = GrammarApi.with(this.axiosInstance);

      api.getObservationTypes().then((data) => {
        console.info("Refresh local obs types");
        localStorage.setItem("observationTypes", JSON.stringify(data));
      });

      api.getParamTypes().then((data) => {
        console.info("Refresh local param types");
        localStorage.setItem("paramTypes", JSON.stringify(data));
      });

      api.getPartParamTypes().then((data) => {
        console.info("Refresh local part param types");
        localStorage.setItem("partParamTypes", JSON.stringify(data));
      });
    }
  }

  get isAuth() {
    return localStorage.getItem(STORAGE_TOKEN_KEY) !== null;
  }

  whoAmI() {
    return { login: localStorage.getItem(STORAGE_LOGIN_KEY) };
  }

  logout() {
    this.axiosInstance
      .post("/auth/logout", {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      })
      .then((resp) => {
        localStorage.clear();
        this.setState({ _isAuth: false });
      })
      .catch((err) => {
        console.error("could not logout", err);
      });
  }

  isRole(role: string): boolean {
    if (this.isAuth) {
      const roleStr = localStorage.getItem(STORAGE_AUTH_ROLE_KEY);
      if (roleStr) {
        const roleArr = roleStr.split(",");
        return roleArr.length == 1 && role == roleArr[0];
      }
    }

    return false;
  }

  hasRole(role: string): boolean {
    if (this.isAuth) {
      const roleStr = localStorage.getItem(STORAGE_AUTH_ROLE_KEY);
      if (roleStr) {
        const roleArr = roleStr.split(",");
        for (const r of roleArr) {
          if (r === role) return true;
        }
      }
    }

    return false;
  }

  hasAnyRole(roles: string[]): boolean {
    for (const role of roles) {
      if (this.hasRole(role)) {
        return true;
      }
    }

    return false;
  }

  login(evt: FormEvent<any>) {
    evt.preventDefault();

    const params = new URLSearchParams();
    params.append("username", this.state.login);
    params.append("password", this.state.password);

    this.axiosInstance
      .post("/auth/login", params, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      })
      .then((resp) => {
        const authTok: string = resp.data.token;
        const expireAt: string = resp.data.expireAt;

        if (authTok) {
          this.onNewToken(authTok);
          localStorage.setItem(STORAGE_TOKEN_KEY, authTok);
          localStorage.setItem(STORAGE_TOKEN_EXP_KEY, expireAt);
          localStorage.setItem(STORAGE_AUTH_ROLE_KEY, resp.data.roles.join(","));
          localStorage.setItem(STORAGE_LOGIN_KEY, resp.data.login);
          this.setState(
            {
              _isAuth: true,
              login: resp.data.login,
              password: "",
              expiration: moment(new Date(expireAt)),
            },
            () => this.refresh()
          );
        } else {
          return Promise.reject("Not authenticated");
        }
      })
      .catch((error) => {
        if (error.response.status == 401 && error.response.data.message) {
          MessageService.error(error.response.data.message);
        }
      });
  }

  renewToken(): Promise<void> {
    if (!this.isAuth) {
      return;
    }

    return this.axiosInstance.post("/auth/renew").then((resp) => {
      const newToken: RenewedToken = resp.data;

      if (newToken) {
        this.onNewToken(newToken.token);
        localStorage.setItem(STORAGE_TOKEN_KEY, newToken.token);
        localStorage.setItem(STORAGE_TOKEN_EXP_KEY, newToken.expireAt);
        this.setState({ expiration: moment(new Date(newToken.expireAt)) });
      } else {
        return Promise.reject("Not authenticated");
      }
    });
  }

  render() {
    if (this.state._isAuth) {
      return (
        <AuthContext.Provider value={this}>
          <ActivityWatcher
            onActive={() => this.renewToken()}
            onInactive={() => this.logout()}
            expiration={this.state.expiration}
          />
          {this.props.children}
        </AuthContext.Provider>
      );
    } else {
      return (
        <div className="container">
          <div className="row">
            <div className="col-6">
              <form onSubmit={this.login.bind(this)}>
                <div className="form-group">
                  <label>{i18n.t("global.username")}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={this.state.login}
                    onChange={(evt) => this.setState({ login: evt.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <Trans i18nKey={"global.password"} />
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    value={this.state.password}
                    onChange={(evt) => this.setState({ password: evt.target.value })}
                  />
                </div>
                <button type="submit" className="btn btn-primary">
                  {i18n.t("global.login")}
                </button>
              </form>
            </div>
          </div>
        </div>
      );
    }
  }

  private onNewToken(newTokenValue: string) {
    CornerstoneUtil.configureWADOImageLoader("Bearer " + newTokenValue);
  }
}
